import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import { routerFactory } from './router'

import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'

import '@fontsource/inter'
import '@fontsource/inter/700.css'

import '@fontsource/bowlby-one-sc/400.css'

import '@fontsource/be-vietnam-pro/400.css'
import '@fontsource/be-vietnam-pro/700.css'

import './assets/index.css'

import { deviceApiFactory } from './libs/deviceApi/deviceApiFactory'
import { MobileMissionsApiClient } from './generated/api/MobileMissionsApiClient'
import { configFromEnv, initSentry } from './libs/sentry'
import { initI18n } from './locales/i18n'
import { MobileAuthApiClient } from './generated/mobileAuthApi'
import { getEnv } from './libs/env'
import { buildCustomAxiosHttpRequest } from './api/HttpRequestCustomAxios'
import { axiosForMobileMissions } from './api/axiosForMobileMissions'
import { buildCustomAxiosHttpRequestMobileAuth } from './api/HttpRequestCustomAxiosMobileAuth'
import { axiosForMobileAuth } from './api/axiosForMobileAuth'
import { TokenProviderDeviceApi } from './api/tokenProviderDeviceApi'
import { resolveLanguage } from './libs/locale'
import { provideDeviceApi } from './libs/deviceApi/provider'
import { provideMobileMissionsApiClient } from './api/mobileMissionsApiProvider'
import { setupRootLogger } from './libs/logging'
import log from 'loglevel'
import { isCacheDisabledParamDefined, getUtmContent, getUtmSource } from './libs/urlParameters'
import { logToDocumentHead as logToDocumentHead } from './libs/domLogger'
import { provideMobileAuthApiClient } from './api/mobileAuthApiProvider'
import { provideI18nGlobal } from './locales/provider'
import { buildClientInfo } from './libs/deviceApi/clientInfo'

async function main() {
  if (getEnv('debugConsoleEnabled')) {
    // Wait for Eruda initialization so any future logs are also logged
    // within Eruda's provided console.
    const Eruda = await import('eruda')
    Eruda.default.init()
  }

  if (getEnv('loggingEnabled')) {
    setupRootLogger('debug')
    log.debug('Logger enabled')
  }

  const useAxiosCache = !isCacheDisabledParamDefined()

  const deviceApi = await deviceApiFactory()
  // Extract entrypoint from URL query params (utm_content or utm_source fallback)
  const entrypointOnPageLoad = getUtmContent() ?? getUtmSource()

  const i18n = initI18n()

  i18n.global.locale.value = resolveLanguage(
    window.navigator.languages,
    i18n.global.availableLocales,
    'en-US'
  )

  const app = createApp(App)

  const apiClient = new MobileMissionsApiClient(
    { BASE: getEnv('apiBaseUrl') },
    buildCustomAxiosHttpRequest(
      axiosForMobileMissions(
        new TokenProviderDeviceApi(deviceApi),
        buildClientInfo(deviceApi),
        entrypointOnPageLoad,
        () => i18n.global.locale.value,
        useAxiosCache
      )
    )
  )

  const mobileAuthApiBaseUrl = getEnv('mobileAuthApiBaseUrl')
  const mobileAuthApiClient = new MobileAuthApiClient(
    { BASE: mobileAuthApiBaseUrl },
    buildCustomAxiosHttpRequestMobileAuth(axiosForMobileAuth(mobileAuthApiBaseUrl))
  )

  provideMobileMissionsApiClient(app, apiClient)
  provideMobileAuthApiClient(app, mobileAuthApiClient)

  provideDeviceApi(app, deviceApi)
  provideI18nGlobal(app, i18n.global)

  const router = routerFactory(deviceApi)
  initSentry(configFromEnv(), app, router)

  app.use(i18n)
  app.use(createPinia())
  app.use(router)

  app.mount('#app')
}

try {
  main()
} catch (error: unknown) {
  // If there is some error thrown from main on app needed - log it to
  // document.head so it can be inspected.
  if (getEnv('env') !== 'prod') {
    logToDocumentHead(String(error))
  }

  throw error
}
